import React, { useState, useEffect } from 'react'

import { Link, useLocation } from "react-router-dom";
import LogoSvg from './LogoSvg';

const NavBar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState('/');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(totalHeight > 0 ? (window.scrollY / totalHeight) * 100 : 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setActiveSection(location.pathname);
  }, [location]);

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About' },
    { to: '/services', label: 'Services' },
    { to: '/projects', label: 'Projects' },
    { to: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => activeSection === path;

  return (
    <>
      {/* Scroll Progress Bar */}
      <div
        className="scroll-progress"
        style={{ width: `${scrollProgress}%` }}
      />

      <header
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${
          scrolled
            ? 'glass-strong shadow-lg shadow-black/20'
            : 'bg-primary-bg/80 backdrop-blur-sm'
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" aria-label="Main navigation">
          <div className="flex items-center justify-between h-16">
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center group" aria-label="Nang Myintzu - Home">
                <div className="transition-transform duration-300 group-hover:scale-105">
                  <LogoSvg width={60} />
                </div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:block">
              <div className="flex items-baseline space-x-1">
                {navLinks.map(({ to, label }) => (
                  <Link
                    key={to}
                    to={to}
                    className={`nav-link-hover px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                      isActive(to)
                        ? 'text-accent bg-accent/10 shadow-inner'
                        : 'text-gray hover:text-white hover:bg-white/5'
                    }`}
                    aria-current={isActive(to) ? 'page' : undefined}
                  >
                    {label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                className="text-gray hover:text-accent p-2 rounded-lg transition-all duration-300 hover:bg-accent/10 active:scale-95"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-expanded={isMenuOpen}
                aria-controls="mobile-menu"
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              >
                <div className="relative w-6 h-6">
                  <span
                    className={`absolute left-0 w-6 h-0.5 bg-current transition-all duration-300 ${
                      isMenuOpen ? 'top-3 rotate-45' : 'top-1'
                    }`}
                  />
                  <span
                    className={`absolute left-0 w-6 h-0.5 bg-current transition-all duration-300 top-3 ${
                      isMenuOpen ? 'opacity-0 scale-x-0' : 'opacity-100'
                    }`}
                  />
                  <span
                    className={`absolute left-0 w-6 h-0.5 bg-current transition-all duration-300 ${
                      isMenuOpen ? 'top-3 -rotate-45' : 'top-5'
                    }`}
                  />
                </div>
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          <div
            id="mobile-menu"
            className={`md:hidden transition-all duration-500 overflow-hidden ${
              isMenuOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="py-2 space-y-1 glass-card rounded-xl mt-2 p-3">
              {navLinks.map(({ to, label }, index) => (
                <Link
                  key={to}
                  to={to}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 ${
                    isActive(to)
                      ? 'text-accent bg-accent/10'
                      : 'text-gray hover:text-white hover:bg-white/5'
                  }`}
                  style={{
                    animation: isMenuOpen ? `fadeInUp 0.3s ${index * 0.05}s ease-out forwards` : 'none',
                    opacity: isMenuOpen ? undefined : 0,
                  }}
                  onClick={() => setIsMenuOpen(false)}
                  aria-current={isActive(to) ? 'page' : undefined}
                >
                  {label}
                </Link>
              ))}
            </div>
          </div>
        </nav>
      </header>
    </>
  );
};

export default NavBar;
